"use client";

import { FormEvent, useState } from "react";

type Form_Status = "idle" | "sending" | "sent";

const Inquiry_Options = [
  "General Inquiry",
  "Technical Support / Bug Report",
  "Licensing & Partnerships",
  "Data Discrepancy",
] as const;

const Label_Class = "font-mono text-xs uppercase tracking-widest text-pq-fg-subtle";

const Input_Class =
  "mt-2 w-full rounded-lg border border-white/15 bg-pq-panel-deep px-3.5 py-2.5 text-sm text-slate-100 outline-none placeholder:text-pq-fg-subtle transition focus:border-cyan-400/50 focus:ring-1 focus:ring-cyan-400";

export function Contact_Support_Form() {
  const [Email, Email_Set] = useState("");
  const [Inquiry_Type, Inquiry_Type_Set] = useState<(typeof Inquiry_Options)[number]>(Inquiry_Options[0]);
  const [Message, Message_Set] = useState("");
  const [Status, Status_Set] = useState<Form_Status>("idle");

  function Form_Reset() {
    Email_Set("");
    Inquiry_Type_Set(Inquiry_Options[0]);
    Message_Set("");
    Status_Set("idle");
  }

  async function Form_Submit(Event: FormEvent<HTMLFormElement>) {
    Event.preventDefault();
    if (Status === "sending") {
      return;
    }

    Status_Set("sending");
    await new Promise((Resolve) => window.setTimeout(Resolve, 900));
    Status_Set("sent");
  }

  return (
    <div className="mt-8 rounded-2xl border border-white/15 bg-pq-panel shadow-md shadow-black/35">
      <div className="h-1 rounded-t-2xl bg-gradient-to-r from-cyan-400 to-amber-400" />

      {Status === "sent" ? (
        <div className="flex flex-col items-center px-6 py-10 text-center md:px-8 md:py-12">
          <span
            className="mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-cyan-500/15 text-cyan-300 ring-1 ring-cyan-400/30"
            aria-hidden
          >
            <svg className="h-7 w-7" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25">
              <path d="M5 13l4 4L19 7" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </span>
          <h2 className="font-display text-2xl font-semibold tracking-tight text-slate-100">Request Submitted</h2>
          <p className="mt-3 max-w-md text-sm leading-relaxed text-slate-300">
            We have received your message and will respond shortly.
          </p>
          <button
            type="button"
            onClick={Form_Reset}
            className="mt-8 rounded-lg border border-white/20 px-5 py-2.5 text-sm font-semibold text-white transition hover:border-cyan-400/50 hover:bg-pq-bg-elevated/5"
          >
            Submit another request
          </button>
        </div>
      ) : (
        <div className="p-6 md:p-8">
          <p className={Label_Class}>Support & inquiries</p>
          <h2 className="mt-2 font-display text-2xl font-semibold tracking-tight text-slate-100 sm:text-3xl">
            Submit a Request
          </h2>

          <form onSubmit={Form_Submit} className="mt-6 space-y-5">
            <div className="grid gap-5 md:grid-cols-2">
              <div>
                <label htmlFor="contact-email" className={Label_Class}>
                  Email address
                </label>
                <input
                  id="contact-email"
                  type="email"
                  required
                  autoComplete="email"
                  placeholder="name@company.com"
                  value={Email}
                  onChange={(Event) => Email_Set(Event.target.value)}
                  disabled={Status === "sending"}
                  className={Input_Class}
                />
              </div>

              <div>
                <label htmlFor="contact-inquiry" className={Label_Class}>
                  Inquiry type
                </label>
                <select
                  id="contact-inquiry"
                  value={Inquiry_Type}
                  onChange={(Event) => Inquiry_Type_Set(Event.target.value as (typeof Inquiry_Options)[number])}
                  disabled={Status === "sending"}
                  className={`${Input_Class} appearance-none`}
                >
                  {Inquiry_Options.map((Option) => (
                    <option key={Option} value={Option}>
                      {Option}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label htmlFor="contact-message" className={Label_Class}>
                How can we help?
              </label>
              <textarea
                id="contact-message"
                required
                rows={4}
                placeholder="Describe your issue, feedback, or inquiry in detail..."
                value={Message}
                onChange={(Event) => Message_Set(Event.target.value)}
                disabled={Status === "sending"}
                className={`${Input_Class} resize-y`}
              />
            </div>

            <button
              type="submit"
              disabled={Status === "sending"}
              className="rounded-lg bg-[#00b4d8] px-6 py-3 text-sm font-semibold text-[#060d18] transition hover:bg-[#22d3ee] hover:shadow-[0_0_15px_rgba(6,182,212,0.4)] disabled:cursor-not-allowed disabled:opacity-70"
            >
              {Status === "sending" ? "Sending..." : "Submit request"}
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
